import { useState, useEffect } from "react";
import { Box, Link as MuiLink, Chip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";
import { imagepathReturn } from "../../config/formatters.ts";

interface Disclosure {
  id: number;
  title: string;
  icon: string;
  link_text: string;
  link_url: string;
  view_mode: string;
  sort_order: number;
  status: boolean;
  document_type: string;
}

export default function MandatoryDisclosure() {
  const navigate = useNavigate();
  const [disclosures, setDisclosures] = useState<Disclosure[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getDisclosures();
  }, [page, limit, search]);

  const getDisclosures = async () => {
    setLoading(true);
    const response = await request.post("mandatory-disclosures", {
      search: search,
      limit,
      offset: page,
      orderColumn: "sort_order",
      orderDirection: "desc",
    });
    setDisclosures(response.data.data);
    setTotalCount(response.data.total);
    setLoading(false);
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "title",
      label: "Title",
      minWidth: 180,
      format: (value) => value,
    },
    {
      id: "document_type",
      label: "Type",
      minWidth: 120,
      format: (value) => (
        <Chip
          size="small"
          label={value === "file_upload" ? "File Upload" : "External Link"}
          sx={{
            fontSize: 11,
            fontWeight: 600,
            bgcolor:
              value === "file_upload"
                ? "rgba(90, 68, 255, 0.12)"
                : "rgba(234, 179, 8, 0.12)",
            color:
              value === "file_upload"
                ? "#5a44ff"
                : "#b45309",
          }}
        />
      ),
    },
    {
      id: "link_text",
      label: "Link Text",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "link",
      label: "Link",
      minWidth: 220,
      format: (_value, row) => {
        if (row?.document_type === "file_upload") {
          return row?.link_url ? (
            <MuiLink
              href={imagepathReturn(row.link_url)}
              target="_blank"
              rel="noopener"
              underline="hover"
              sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
            >
              <InsertDriveFileIcon sx={{ fontSize: 16 }} />
              View file
            </MuiLink>
          ) : (
            "—"
          );
        }
        return row?.link_url ? (
          <MuiLink
            href={row.link_url}
            target={row?.view_mode === "new_tab" ? "_blank" : "_self"}
            underline="hover"
          >
            {row.link_url}
          </MuiLink>
        ) : (
          "—"
        );
      },
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 120,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/mandatory-disclosure/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({ open: true, id: row.id, title: row?.title }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Mandatory Disclosures"
        showSearch
        searchPlaceholder="Search disclosures..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Disclosure",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/mandatory-disclosure/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={disclosures}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Disclosure - " + deleteDialog?.title}
          message="Are you sure you want to delete this disclosure? This action cannot be undone."
          apiEndpoint={"mandatory-disclosures/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getDisclosures();
          }}
        />
      </PageWrapper>
    </>
  );
}